import { PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { ThunkDispatch } from 'redux-thunk';
import { troubleshootSystem } from '../components/toolDiagnosis/utils';
import { fetchPtDir } from '../store/actions/app';
import {
    fetchDoc,
    fetchDoctorAccounts,
    UpdateMyProfileAction,
    fetchMyProfilesAction,
} from '../store/actions/login';
import {
    amIDoctor,
    getAllPatients,
    getDoc,
    getMe,
    setPtCount,
    getMySingleDoctorId,
    isSingleDoctorAccess,
} from '../store/idbHandler';

import { GlobalState } from '../store/reducers';
import { GO_TO_LOGOUT, GO_TO_REDIRECT_IPAD } from '../routes/paths';
import { doctorIds } from '../constants';
import { track } from '../tracking';
import { dumpDynamicFormAutoSuggest, LS_NEW_PT_DF_AS } from './utils';
import setupRedux from '../store/setupAppRedux';
import { captureErrorString } from './captureError';
import { getDefiniteMActiveId, getMActiveId } from '../store/localstorage';
import { initFirebaseAuth } from '../firestore';

interface Props extends RouteComponentProps {
    dispatch: ThunkDispatch<GlobalState, null, UpdateMyProfileAction>;
}

class SetupApp extends PureComponent<Props> {
    componentDidMount = (): void => {
        setPtCount();

        localStorage.removeItem(LS_NEW_PT_DF_AS);

        try {
            const onFailure = () => {
                alert('Associated doctor profile not found. Please contact contact@eka.care');
                this.props.history.replace(GO_TO_LOGOUT);
            };
            const docId = localStorage.getItem('activeId');
            if (!docId) {
                return;
            }

            // if (
            //     ((navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 0) ||
            //         navigator.platform === 'iPad') &&
            //     doctorIds.indexOf(docId) === -1
            // ) {
            //     track('BLOCKED_USER_TO_ACCESS_STETHO', {
            //         dddId: docId,
            //         swidth: window.innerWidth.toString(),
            //     });
            //     this.props.history.push(GO_TO_REDIRECT_IPAD);
            //     return;
            // }

            const { dispatch } = this.props;

            const onCompleteSetup = () => {
                const forwardHref = localStorage.getItem('forwardHref');
                if (forwardHref) {
                    this.props.history.replace(forwardHref.replace(window.location.origin, ''));
                    localStorage.removeItem('forwardHref');
                } else {
                    this.props.history.replace('/');
                }
            };

            const onSuccess = async () => {
                const promises: Promise<unknown>[] = [initFirebaseAuth()];

                const setupPromise = new Promise<void>(async (resolve, reject) => {
                    try {
                        try {
                            await fetchPtDir();
                        } catch (e) {
                            console.error('Error fetching ptDir', e);
                        }

                        const amIDoctorId = getMe()?.doctor?._id;

                        if (amIDoctor() && amIDoctorId) {
                            // usual dr flows with docid in JWT
                            await setupRedux(
                                dispatch,
                                amIDoctorId,
                                () => {
                                    resolve();
                                    return;
                                },
                                () => {
                                    console.log('Access control success');
                                },
                                undefined,
                                getDefiniteMActiveId(),
                            );
                        } else if (isSingleDoctorAccess() && getMySingleDoctorId()) {
                            // new Azhar flow
                            await setupRedux(
                                dispatch,
                                getMySingleDoctorId() || '',
                                () => {
                                    resolve();
                                    return;
                                },
                                () => {
                                    console.log('Access control success');
                                },
                                undefined,
                                getDefiniteMActiveId(),
                            );
                        } else {
                            // multi doctor access
                            await setupRedux(
                                dispatch,
                                getMActiveId() || '',
                                () => {
                                    resolve();
                                    return;
                                },
                                () => {
                                    console.log('Access control success');
                                },
                                undefined,
                                getDefiniteMActiveId(),
                            );
                        }

                        const doc = getDoc();
                        if (doc && doc.loginmeta?.role === 'DOCTOR') {
                            troubleshootSystem(doc);
                        }

                        const patients = await getAllPatients();
                        patients && dumpDynamicFormAutoSuggest(patients);
                    } catch (e) {
                        reject(e);
                    }
                });
                promises.push(setupPromise);

                await Promise.all(promises);
                onCompleteSetup();
            };
            this.props.dispatch(fetchDoc(docId, onSuccess, onFailure));
            this.props.dispatch(fetchDoctorAccounts());
            this.props.dispatch(fetchMyProfilesAction(true));
        } catch (e) {
            console.error('Error setting up workspace', e);
            captureErrorString(`Error setting up workspace: ${(e as Error).message}`);
        }
    };

    render(): ReactNode {
        return (
            <div className="flex flex-col items-center justify-center w-screen h-screen space-y-64">
                <div className="pulse">
                    <svg width="16px" height="12px">
                        <polyline id="back" points="1 6 4 6 6 11 10 1 12 6 15 6"></polyline>
                        <polyline id="front" points="1 6 4 6 6 11 10 1 12 6 15 6"></polyline>
                    </svg>
                </div>
                <p className="text-24 text-neutral-black">Setting up workspace...</p>
            </div>
        );
    }
}

export default connect()(SetupApp);
